import { useI18n } from '../i18n';
import { usePushEnableMessage, usePushSubscription } from '../hooks/usePushSubscription';
import { BellIcon } from './shared/Icons';

// #557 — one line for a person whose device has no push subscription, at
// the place where the pushed notices matter:
//   - a manager: above the End button of the Weigh-in tab and the Check-in
//     tab (the End proposal and the End summary push);
//   - an official in the referee pool: on the Officials tab (the call to the
//     ring and the seat list push).
// The opt-in banner shows to the athletes and the coaches only
// (PushPromptBanner, the #495 decision), so the device of a manager or an
// official often has no subscription, and the notices reach the in-app list
// only. The line is not a new banner: it shows inside the tab.
//
// It shows when push is supported, the device does not deny it, and the
// device has no subscription. It waits until the push store reads the
// subscription of the device, so a subscribed device never shows it for one
// moment.
//
// A failed Enable shows an error toast (usePushEnableMessage): the line then
// stays, or goes away when the browser blocked push.

interface PushOffHintProps {
  /** The text of the line: what this person misses with push off. The
   *  default is the text of the End tabs. */
  text?: string;
}

export default function PushOffHint({ text }: PushOffHintProps) {
  const { t } = useI18n();
  const push = usePushSubscription();
  const explainEnable = usePushEnableMessage();
  if (!push.supported || push.permission === 'denied' || !push.checked || push.enabled) return null;
  return (
    <div data-testid="push-off-hint"
      className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-800">
      <BellIcon className="w-4 h-4 shrink-0 text-blue-600" />
      <span className="flex-1 min-w-0">{text ?? t.notifications.pushOffHint}</span>
      <button type="button" data-testid="push-off-hint-enable" onClick={() => { void push.enableWithResult().then(explainEnable); }}
        disabled={push.loading} className="btn-outline btn-sm shrink-0">
        {push.loading ? '…' : t.notifications.pushPrompt.enable}
      </button>
    </div>
  );
}
